// USSD (4 October 2026): a business's flows answering a USSD code as numbered menus. A business applies for a
// code, either an extension on the platform's shared code (*120*1234*56#, approved in days) or a dedicated code
// of its own (*120*5678#, weeks). Charged from credits: a set-up fee when the code goes live (held from bought
// credits while the application waits), a rental on the 1st (the first part-month prorated), and each session.
//
// `services/ussd`. Hand-typed until the generated client is next fetched from a running stack. The request path
// is the house core, so the envelope, token refresh and ApiError mapping are the same as everywhere.
//
// A console asks `getStatus()` before it offers anything USSD: `available: false` (with `reason`) means USSD is
// switched off, no aggregator is set up, or a price is missing, and applying is refused with
// `USSD_APPLICATIONS_CLOSED`.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';

export type UssdCodeKind = 'extension' | 'dedicated';
export type UssdCodeStatus = 'live' | 'suspended' | 'released';
export type UssdApplicationStatus = 'submitted' | 'approved' | 'rejected' | 'withdrawn';

/** One kind of code: whether it can be applied for, and what it costs this organisation (minor units, VAT in). */
export interface UssdKindOffer {
  kind: UssdCodeKind;
  open: boolean;
  setup_minor: number;
  monthly_minor: number;
  /** Why it cannot be applied for, in words. */
  reason?: string | null;
}

export interface UssdCode {
  id: string;
  /** Platform console only. */
  organisation_id?: string | null;
  kind: UssdCodeKind;
  /** As a person dials it: `*120*1234*56#`. */
  code: string;
  extension?: string | null;
  status: UssdCodeStatus;
  suspended_reason?: string | null;
  /** Platform console only: the aggregator the code is held with. */
  provider_code?: string | null;
  /** When the next rental falls due: a 1st, midnight South African time. */
  next_rental_at?: string | null;
  /** Since when a rental has gone unpaid. After the grace days the code stops answering. */
  overdue_since?: string | null;
  approved_at?: string | null;
  released_at?: string | null;
  created_at: string;
}

export interface UssdApplication {
  id: string;
  /** Platform console only. */
  organisation_id?: string | null;
  kind: UssdCodeKind;
  status: UssdApplicationStatus;
  preferred_digits?: string | null;
  purpose: string;
  terms_version?: string | null;
  fee_minor: number;
  monthly_minor: number;
  /** The set-up fee is held from bought credits while the application waits. */
  fee_held: boolean;
  submitted_by?: string | null;
  decided_at?: string | null;
  decided_by?: string | null;
  /** Shown to the organisation when the platform rejects it. */
  reject_reason?: string | null;
  code_id?: string | null;
  code?: string | null;
  created_at: string;
}

export interface UssdStatus {
  available: boolean;
  reason?: string | null;
  /** What one session costs this organisation. */
  session_minor: number;
  /** The platform's shared code, when set: extensions are dialled on it. */
  shared_code?: string | null;
  kinds: UssdKindOffer[];
  codes: UssdCode[];
  /** Applications waiting for a decision. */
  applications: UssdApplication[];
}

export interface UssdQuote {
  kind: UssdCodeKind;
  open: boolean;
  reason?: string | null;
  setup_minor: number;
  monthly_minor: number;
  session_minor: number;
  /** If approved today: the part of this month left, charged with the set-up fee. */
  first_month_minor: number;
  /** "4 to 31 October 2026". */
  first_month_period: string;
  /** "1 November 2026": the full rental from then on, on each 1st. */
  next_rental_on: string;
  terms_version: string;
}

export interface UssdApplyRequest {
  kind: UssdCodeKind;
  /** Up to six digits, not starting with 0: a wish, not a promise. */
  preferred_digits?: string;
  /** What the code is for, 10 to 1 000 characters. The networks ask. */
  purpose: string;
  accept_terms: boolean;
}

export interface UssdSessionLimits {
  /** Characters on one screen: 60 to 182, 160 by default. */
  max_chars: number;
  /** How long a flow's turn may take, 1 000 to 8 000 ms. */
  turn_timeout_ms: number;
  /** Screens in one session, 2 to 100. */
  max_turns: number;
}

export interface UssdAdminSettings {
  master_enabled: boolean;
  /** Such as `*120*1234#`; empty until set. */
  shared_code: string;
  terms_version: string;
  rental_grace_days: number;
  release_after_days: number;
  session_limits: UssdSessionLimits;
  routing: { configured: boolean; active_provider?: string | null; reason?: string | null };
  /** The callback address to give the aggregator for each code. */
  callback_url: string;
  /** Which of the five USSD prices exist; `priced` is null when billing did not answer. */
  prices: { sku: string; priced: boolean | null; amount_minor?: number | null }[];
  updated_at: string;
  updated_by?: string | null;
}

export interface UssdAdminSettingsUpdate {
  master_enabled?: boolean;
  /** Dialled like `*120*1234#`; empty clears it. Refused while extensions on the current one are in use. */
  shared_code?: string;
  terms_version?: string;
  rental_grace_days?: number;
  release_after_days?: number;
  session_limits?: Partial<UssdSessionLimits>;
}

export interface UssdApproveRequest {
  /** A dedicated code, as dialled: `*120*5678#`. */
  code?: string;
  /** An extension's digits on the shared code. */
  extension?: string;
  /** The aggregator; the routing's active one when omitted. */
  provider_code?: string;
}

export interface UssdApproveResult extends UssdApplication {}

export interface UssdSessionsSummary {
  since: string;
  sessions: number;
  by_state: { state: string; count: number }[];
  by_outcome: { outcome?: string | null; count: number }[];
  charged_minor: number;
  /** Distinct numbers that dialled. */
  people: number;
  turns: number;
  by_code: { code_id: string; code?: string | null; sessions: number }[];
  last_webhook?: { provider_code: string; received_at: string; outcome?: string | null } | null;
}

export function getStatus(): Promise<Envelope<UssdStatus>> {
  return http.get<Envelope<UssdStatus>>('/v1/ussd/status');
}

export function quote(kind: UssdCodeKind): Promise<Envelope<UssdQuote>> {
  return http.get<Envelope<UssdQuote>>('/v1/ussd/applications/quote', { query: { kind } });
}

export function listApplications(): Promise<Envelope<UssdApplication[]>> {
  return http.get<Envelope<UssdApplication[]>>('/v1/ussd/applications');
}

/**
 * Owner and admins only (an agent is refused 403). Holds the set-up fee from bought credits:
 * 402 `USSD_NO_CREDIT`, 409 `USSD_APPLICATION_OPEN`, 409 `USSD_APPLICATIONS_CLOSED`, 400 `USSD_TERMS_REQUIRED`.
 */
export function apply(req: UssdApplyRequest): Promise<Envelope<UssdApplication>> {
  return http.post<Envelope<UssdApplication>>('/v1/ussd/applications', { body: req });
}

/** Owner and admins only. The held fee is given back. */
export function withdraw(id: string): Promise<Envelope<UssdApplication>> {
  return http.post<Envelope<UssdApplication>>(`/v1/ussd/applications/${encodeURIComponent(id)}/withdraw`);
}

export function listCodes(): Promise<Envelope<UssdCode[]>> {
  return http.get<Envelope<UssdCode[]>>('/v1/ussd/codes');
}

/** Platform console: the switch, the shared code, rental rules, session limits and what is missing. */
export function adminSettings(): Promise<Envelope<UssdAdminSettings>> {
  return http.get<Envelope<UssdAdminSettings>>('/v1/admin/ussd/settings');
}

export function updateAdminSettings(req: UssdAdminSettingsUpdate): Promise<Envelope<UssdAdminSettings>> {
  return http.put<Envelope<UssdAdminSettings>>('/v1/admin/ussd/settings', { body: req });
}

export function adminApplications(status?: UssdApplicationStatus): Promise<Envelope<UssdApplication[]>> {
  return http.get<Envelope<UssdApplication[]>>('/v1/admin/ussd/applications', { query: status ? { status } : undefined });
}

/** Assign the code and take the held set-up fee; the rest of this month's rental is charged too. */
export function approve(id: string, req: UssdApproveRequest): Promise<Envelope<UssdApproveResult> & { first_rental?: string; code?: UssdCode }> {
  return http.post<Envelope<UssdApproveResult> & { first_rental?: string; code?: UssdCode }>(
    `/v1/admin/ussd/applications/${encodeURIComponent(id)}/approve`, { body: req });
}

/** A reason the organisation is shown is required. The held fee is given back. */
export function reject(id: string, reason: string): Promise<Envelope<UssdApplication>> {
  return http.post<Envelope<UssdApplication>>(`/v1/admin/ussd/applications/${encodeURIComponent(id)}/reject`, { body: { reason } });
}

export function adminCodes(status?: UssdCodeStatus): Promise<Envelope<UssdCode[]>> {
  return http.get<Envelope<UssdCode[]>>('/v1/admin/ussd/codes', { query: status ? { status } : undefined });
}

export function suspend(id: string, reason?: string): Promise<Envelope<UssdCode>> {
  return http.post<Envelope<UssdCode>>(`/v1/admin/ussd/codes/${encodeURIComponent(id)}/suspend`, { body: reason ? { reason } : {} });
}

export function resume(id: string): Promise<Envelope<UssdCode>> {
  return http.post<Envelope<UssdCode>>(`/v1/admin/ussd/codes/${encodeURIComponent(id)}/resume`);
}

/** The code stops answering and is no longer charged. */
export function release(id: string): Promise<Envelope<UssdCode>> {
  return http.post<Envelope<UssdCode>>(`/v1/admin/ussd/codes/${encodeURIComponent(id)}/release`);
}

/** The last day's sessions. */
export function adminSessionsSummary(): Promise<Envelope<UssdSessionsSummary>> {
  return http.get<Envelope<UssdSessionsSummary>>('/v1/admin/ussd/sessions/summary');
}
